import React, { useState } from 'react';

import {
  PlusOutlined,
  DeleteOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import {
  Typography,
  InputNumber,
  Switch,
  Checkbox,
  Input,
  Button,
  Tooltip,
  Divider,
  Space,
} from 'antd';

import {
  YandexDirectGenerationSettings,
  YandexDirectFieldLimits,
  GenerationRule,
  DEFAULT_YANDEX_DIRECT_LIMITS,
  DEFAULT_YANDEX_DIRECT_RULES,
} from '../types';

const { Text } = Typography;
const { TextArea } = Input;

// --- Field config ---

interface FieldConfig {
  key: keyof YandexDirectFieldLimits;
  label: string;
  tooltip: string;
  suffix: string;
  min: number;
  max: number;
}

const FIELD_CONFIGS: FieldConfig[] = [
  {
    key: 'title1MaxChars',
    label: 'Заголовок 1',
    tooltip: 'Макс. 56 символов (с пробелами). Одно слово не длиннее 22 символов. Основной заголовок — синий, самый заметный.',
    suffix: 'симв.',
    min: 10,
    max: 56,
  },
  {
    key: 'title1MaxWordChars',
    label: '↳ макс. длина слова',
    tooltip: 'Одно слово в заголовке 1 не может быть длиннее 22 символов.',
    suffix: 'симв.',
    min: 5,
    max: 22,
  },
  {
    key: 'title2MaxChars',
    label: 'Заголовок 2',
    tooltip: 'Макс. 30 символов + до 15 знаков препинания. Показывается через тире после первого. Не всегда виден — зависит от ширины экрана.',
    suffix: 'симв.',
    min: 10,
    max: 30,
  },
  {
    key: 'title2MaxPunctuation',
    label: '↳ макс. знаков препинания',
    tooltip: 'Дополнительно к 30 символам можно использовать до 15 знаков препинания.',
    suffix: 'шт.',
    min: 0,
    max: 15,
  },
  {
    key: 'textMaxChars',
    label: 'Текст объявления',
    tooltip: 'Макс. 81 символ + до 15 знаков препинания. Одно слово не длиннее 23 символов.',
    suffix: 'симв.',
    min: 20,
    max: 81,
  },
  {
    key: 'textMaxPunctuation',
    label: '↳ макс. знаков препинания',
    tooltip: 'Дополнительно к 81 символу можно использовать до 15 знаков препинания.',
    suffix: 'шт.',
    min: 0,
    max: 15,
  },
  {
    key: 'textMaxWordChars',
    label: '↳ макс. длина слова',
    tooltip: 'Одно слово в тексте объявления не может быть длиннее 23 символов.',
    suffix: 'симв.',
    min: 5,
    max: 23,
  },
  {
    key: 'displayUrlMaxChars',
    label: 'Отображаемая ссылка',
    tooltip: 'Путь после домена (без домена). Макс. 20 символов. Например: /курс-маркетинг.',
    suffix: 'симв.',
    min: 5,
    max: 20,
  },
  {
    key: 'sitelinkTextMaxChars',
    label: 'Быстрая ссылка — заголовок',
    tooltip: 'Кликабельные ссылки под объявлением. Макс. 30 символов на заголовок.',
    suffix: 'симв.',
    min: 5,
    max: 30,
  },
  {
    key: 'sitelinkDescMaxChars',
    label: 'Быстрая ссылка — описание',
    tooltip: 'Текст под заголовком быстрой ссылки. Макс. 60 символов.',
    suffix: 'симв.',
    min: 5,
    max: 60,
  },
  {
    key: 'sitelinksCount',
    label: 'Кол-во быстрых ссылок',
    tooltip: 'Максимум 8 быстрых ссылок на одно объявление.',
    suffix: 'шт.',
    min: 0,
    max: 8,
  },
  {
    key: 'calloutMaxChars',
    label: 'Текст уточнения',
    tooltip: 'Короткие фразы-выгоды. Макс. 25 символов каждое. Общая длина ≤132 (ПК), ≤66 (мобильные).',
    suffix: 'симв.',
    min: 5,
    max: 25,
  },
  {
    key: 'calloutsCount',
    label: 'Кол-во уточнений',
    tooltip: 'Максимум 4 уточнения на объявление.',
    suffix: 'шт.',
    min: 0,
    max: 4,
  },
];

// --- Preview mock ---

const PREVIEW_EXAMPLE = {
  title1: 'А если через год ничего не изменится?',
  title2: 'Начните сейчас',
  url: 'example.com/вебинар-маркетинг',
  text: 'Бесплатный вебинар: как сменить профессию без риска.',
  sitelinks: ['Программа', 'Отзывы', 'Стоимость'],
  callouts: ['Бесплатно', 'Диплом', 'Рассрочка 0%'],
};

// --- Props ---

interface YandexDirectSettingsProps {
  settings: YandexDirectGenerationSettings;
  onChange: (settings: YandexDirectGenerationSettings) => void;
}

// --- Component ---

const YandexDirectSettings: React.FC<YandexDirectSettingsProps> = ({ settings, onChange }) => {
  const [newRuleText, setNewRuleText] = useState('');

  const updateLimits = (key: keyof YandexDirectFieldLimits, value: number) => {
    onChange({
      ...settings,
      fieldLimits: { ...settings.fieldLimits, [key]: value },
    });
  };

  const toggleField = (field: 'generateTitle2' | 'generateDisplayUrl' | 'generateSitelinks' | 'generateSitelinkDesc' | 'generateCallouts') => {
    onChange({ ...settings, [field]: !settings[field] });
  };

  const toggleRule = (ruleId: string) => {
    onChange({
      ...settings,
      rules: settings.rules.map((r) =>
        r.id === ruleId ? { ...r, enabled: !r.enabled } : r
      ),
    });
  };

  const addRule = () => {
    if (!newRuleText.trim()) return;
    const newRule: GenerationRule = {
      id: String(Date.now()),
      text: newRuleText.trim(),
      enabled: true,
    };
    onChange({ ...settings, rules: [...settings.rules, newRule] });
    setNewRuleText('');
  };

  const deleteRule = (ruleId: string) => {
    onChange({
      ...settings,
      rules: settings.rules.filter((r) => r.id !== ruleId),
    });
  };

  const enabledRulesCount = settings.rules.filter((r) => r.enabled).length;

  return (
    <div className="ya-settings">
      {/* Preview */}
      <div className="ya-settings__section">
        <Text strong style={{ fontSize: 13 }}>Превью формата объявления</Text>
        <div className="ya-settings__preview">
          <div className="ya-settings__preview-ad">
            <div className="ya-settings__preview-badge">Реклама</div>
            <div className="ya-settings__preview-title">
              {PREVIEW_EXAMPLE.title1}
              {settings.generateTitle2 && (
                <span className="ya-settings__preview-title2"> — {PREVIEW_EXAMPLE.title2}</span>
              )}
            </div>
            <div className="ya-settings__preview-url">{PREVIEW_EXAMPLE.url}</div>
            <div className="ya-settings__preview-text">{PREVIEW_EXAMPLE.text}</div>
            {settings.generateSitelinks && (
              <div className="ya-settings__preview-sitelinks">
                {PREVIEW_EXAMPLE.sitelinks.map((sl, i) => (
                  <span key={i} className="ya-settings__preview-sitelink">{sl}</span>
                ))}
              </div>
            )}
            {settings.generateCallouts && (
              <div className="ya-settings__preview-callouts">
                {PREVIEW_EXAMPLE.callouts.join(' · ')}
              </div>
            )}
          </div>
        </div>
      </div>

      <Divider style={{ margin: '16px 0' }} />

      {/* Fields to generate */}
      <div className="ya-settings__section">
        <Text strong style={{ fontSize: 13 }}>Какие поля генерировать</Text>
        <div className="ya-settings__toggles">
          <div className="ya-settings__toggle-row">
            <Text style={{ fontSize: 13 }}>Заголовок 1</Text>
            <Switch checked disabled size="small" />
            <Text type="secondary" style={{ fontSize: 11 }}>всегда</Text>
          </div>
          <div className="ya-settings__toggle-row">
            <Text style={{ fontSize: 13 }}>Заголовок 2</Text>
            <Switch
              checked={settings.generateTitle2}
              onChange={() => toggleField('generateTitle2')}
              size="small"
            />
          </div>
          <div className="ya-settings__toggle-row">
            <Text style={{ fontSize: 13 }}>Текст объявления</Text>
            <Switch checked disabled size="small" />
            <Text type="secondary" style={{ fontSize: 11 }}>всегда</Text>
          </div>
          <div className="ya-settings__toggle-row">
            <Text style={{ fontSize: 13 }}>Отображаемая ссылка</Text>
            <Switch
              checked={settings.generateDisplayUrl}
              onChange={() => toggleField('generateDisplayUrl')}
              size="small"
            />
          </div>
          <div className="ya-settings__toggle-row">
            <Text style={{ fontSize: 13 }}>Быстрые ссылки</Text>
            <Switch
              checked={settings.generateSitelinks}
              onChange={() => toggleField('generateSitelinks')}
              size="small"
            />
          </div>
          {settings.generateSitelinks && (
            <div className="ya-settings__toggle-row" style={{ paddingLeft: 16 }}>
              <Text style={{ fontSize: 12, color: '#8c8c8c' }}>↳ Описание быстрых ссылок</Text>
              <Switch
                checked={settings.generateSitelinkDesc}
                onChange={() => toggleField('generateSitelinkDesc')}
                size="small"
              />
            </div>
          )}
          <div className="ya-settings__toggle-row">
            <Text style={{ fontSize: 13 }}>Уточнения</Text>
            <Switch
              checked={settings.generateCallouts}
              onChange={() => toggleField('generateCallouts')}
              size="small"
            />
          </div>
        </div>
      </div>

      <Divider style={{ margin: '16px 0' }} />

      {/* Limits */}
      <div className="ya-settings__section">
        <Text strong style={{ fontSize: 13 }}>Лимиты символов</Text>
        <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 8 }}>
          По{' '}
          <a
            href="https://yandex.ru/support/direct/ru/moderation/technical-restrictions"
            target="_blank"
            rel="noopener noreferrer"
            style={{ fontSize: 11 }}
          >
            правилам Яндекс Директ
          </a>
          . Можно уменьшить для более коротких текстов.
        </Text>
        <div className="ya-settings__limits">
          {FIELD_CONFIGS.map((fc) => {
            // Skip disabled fields
            const title2Keys: string[] = ['title2MaxChars', 'title2MaxPunctuation', 'title2MaxWordChars'];
            if (title2Keys.includes(fc.key) && !settings.generateTitle2) return null;
            if (fc.key === 'displayUrlMaxChars' && !settings.generateDisplayUrl) return null;
            const sitelinkKeys: string[] = ['sitelinkTextMaxChars', 'sitelinkDescMaxChars', 'sitelinksCount'];
            if (sitelinkKeys.includes(fc.key) && !settings.generateSitelinks) return null;
            if (fc.key === 'sitelinkDescMaxChars' && !settings.generateSitelinkDesc) return null;
            const calloutKeys: string[] = ['calloutMaxChars', 'calloutsCount'];
            if (calloutKeys.includes(fc.key) && !settings.generateCallouts) return null;

            return (
              <div key={fc.key} className="ya-settings__limit-row">
                <div className="ya-settings__limit-label">
                  <Text style={{ fontSize: 12 }}>{fc.label}</Text>
                  <Tooltip title={fc.tooltip}>
                    <InfoCircleOutlined style={{ fontSize: 11, color: '#bfbfbf', marginLeft: 4 }} />
                  </Tooltip>
                </div>
                <Space size={4}>
                  <InputNumber
                    size="small"
                    min={fc.min}
                    max={fc.max}
                    value={settings.fieldLimits[fc.key]}
                    onChange={(val) => updateLimits(fc.key, val || fc.min)}
                    style={{ width: 65 }}
                  />
                  <Text type="secondary" style={{ fontSize: 11 }}>{fc.suffix}</Text>
                </Space>
              </div>
            );
          })}
        </div>
      </div>

      <Divider style={{ margin: '16px 0' }} />

      {/* Rules */}
      <div className="ya-settings__section">
        <div className="ya-settings__rules-header">
          <Text strong style={{ fontSize: 13 }}>
            Правила генерации
          </Text>
          <Text type="secondary" style={{ fontSize: 11 }}>
            {enabledRulesCount} из {settings.rules.length} активны
          </Text>
        </div>
        <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 8 }}>
          Инструкции, которые AI будет учитывать при генерации текстов объявлений.
        </Text>

        <div className="ya-settings__rules">
          {settings.rules.map((rule) => (
            <div key={rule.id} className={`ya-settings__rule${rule.enabled ? '' : ' ya-settings__rule--disabled'}`}>
              <Checkbox
                checked={rule.enabled}
                onChange={() => toggleRule(rule.id)}
              />
              <Text
                style={{
                  fontSize: 12,
                  flex: 1,
                  opacity: rule.enabled ? 1 : 0.5,
                  textDecoration: rule.enabled ? 'none' : 'line-through',
                }}
              >
                {rule.text}
              </Text>
              <Tooltip title="Удалить правило">
                <Button
                  type="text"
                  size="small"
                  danger
                  icon={<DeleteOutlined style={{ fontSize: 11 }} />}
                  onClick={() => deleteRule(rule.id)}
                />
              </Tooltip>
            </div>
          ))}
        </div>

        {/* Add rule */}
        <div className="ya-settings__add-rule">
          <TextArea
            value={newRuleText}
            onChange={(e) => setNewRuleText(e.target.value)}
            placeholder="Например: Не использовать слово «уникальный»"
            rows={2}
            style={{ fontSize: 12 }}
          />
          <Button
            size="small"
            icon={<PlusOutlined />}
            onClick={addRule}
            disabled={!newRuleText.trim()}
            style={{ marginTop: 6, borderRadius: 6 }}
          >
            Добавить правило
          </Button>
        </div>
      </div>
    </div>
  );
};

// --- Default settings factory ---

export const getDefaultYandexDirectSettings = (): YandexDirectGenerationSettings => ({
  fieldLimits: { ...DEFAULT_YANDEX_DIRECT_LIMITS },
  rules: DEFAULT_YANDEX_DIRECT_RULES.map((r) => ({ ...r })),
  generateTitle2: true,
  generateDisplayUrl: true,
  generateSitelinks: true,
  generateSitelinkDesc: false,
  generateCallouts: true,
});

export default YandexDirectSettings;
